import { createContext, FC, ReactNode, useContext, useEffect, useState } from "react";
import { SalesOrg, Brand, FooterLink, CountryUrl } from 'models/types';
import siteConfig from 'site-config';
import { useUserCountry } from "contexts/UserCountryProvider";
import { useCart } from "contexts/CartProvider";

export type SiteConfigContextType = {
    salesOrg: SalesOrg;
    getBrand: () => Brand;
    getCountryUrl: () => CountryUrl;
    getFooterLink: () => FooterLink;
    getCurrencyCode: () => string | undefined;
    isProspectCountry: () => boolean;
    getClickHereEmail: () => string;
    getRegistrationLink: () => string;
};

const SiteConfigContextDefaultState: SiteConfigContextType = {
    salesOrg: null as any as SalesOrg,
    getBrand: () => null as any as Brand,
    getCountryUrl: () => null as any as CountryUrl,
    getFooterLink: () => null as any as FooterLink,
    getCurrencyCode: () => '',
    isProspectCountry: () => true,
    getClickHereEmail: () => '',
    getRegistrationLink: () => ''
};

export const SiteConfigContext = createContext(SiteConfigContextDefaultState);

interface SiteConfigProviderProps {
    children: ReactNode;
}

const SiteConfigProvider: FC<SiteConfigProviderProps> = ({ children }) => {
    const defaultSalesOrg: SalesOrg = {
        region: {
            name: '',
            abbrevation: '',
            discription: ''
        },
        salesOrgCode: '',
        salesOrgAlias: '',
        supportedLocales: []
    };
    const [salesOrg, setSalesOrg] = useState(defaultSalesOrg);
    const { region, country, locale } = useUserCountry();
    const { hasCanister, getCart } = useCart();

    const getBrand = () => {
        let data = siteConfig.brandData.find(b => b.salesOrgAlias === salesOrg.salesOrgAlias);
        if (!data) {
            data = siteConfig.brandData.find(b => b.salesOrgAlias === "DEFAULT");
        }
        return data as Brand;
    };
    const getCountryUrl = () => {
        var isEnabledOracleRightNow = siteConfig.OracleRightNowCountries.find(c => c === salesOrg.salesOrgAlias);
        const localeId = salesOrg.supportedLocales.find(l => l.localeCode === locale)?.localeId;
        let data;
        if (isEnabledOracleRightNow) {
            if (localeId) {
                data = siteConfig.oracleRightNow_CountryUrlData.find(c => c.salesOrgAlias === `${salesOrg.salesOrgAlias}|${localeId}`);
            }
            data = !data ? siteConfig.oracleRightNow_CountryUrlData.find(c => c.salesOrgAlias === salesOrg.salesOrgAlias) : data;
        }
        else {
            if (localeId) {
                data = siteConfig.countryUrlData.find(c => c.salesOrgAlias === `${salesOrg.salesOrgAlias}|${localeId}`);
            }
            data = !data ? siteConfig.countryUrlData.find(c => c.salesOrgAlias === salesOrg.salesOrgAlias) : data;
        }

        const defaultLinks = siteConfig.countryUrlData.find(c => c.salesOrgAlias === "DEFAULT") as CountryUrl;

        if (data && data.contactUs !== defaultLinks.contactUs) {
            var env = import.meta.env.VITE_ENV_ABBREVIATION as string;
            data.contactUs =  env.toLowerCase() !== "" && !data.contactUs.includes("--tst2") ? data.contactUs.replace(".custhelp.com", "--tst2.custhelp.com") : data.contactUs; 
            return data as CountryUrl;
        }
        else {
            return defaultLinks;
        }
    };
    const getFooterLink = () => {
        let data = siteConfig.footerLinkData.find(c => c.salesOrgAlias === salesOrg.salesOrgAlias);
        if (!data) {
            const localeId = salesOrg.supportedLocales.find(l => l.localeCode === locale)?.localeId;
            data = siteConfig.footerLinkData.find(c => c.salesOrgAlias === `${salesOrg.salesOrgAlias}|${localeId}`);
        }
        if (data) {
            return data as FooterLink;
        }
        else {
            return siteConfig.footerLinkData.find(c => c.salesOrgAlias === "DEFAULT") as FooterLink;
        }
    };
    const getCurrencyCode = () => {
        if (!salesOrg.salesOrgAlias) return undefined;
        let data = siteConfig.countryCurrency.find(f => f.salesOrgAlias === salesOrg.salesOrgAlias);
        if (!data) {
            data = siteConfig.countryCurrency.find(f => f.salesOrgAlias === "DEFAULT");
        }
        return data?.code;
    };

    const isProspectCountry = () => {
        const data = siteConfig.prospectCountries.find(f => f.toUpperCase() === salesOrg.salesOrgAlias);
        if (!data) {
            return false;
        }
        return true;
    };

    const getRegistrationLink = () => {
        const country = salesOrg.salesOrgAlias === 'UK' ? 'GB' : salesOrg.salesOrgAlias;
        const canisterParam = hasCanister() ? '&isCanister=true' : '';
        return isProspectCountry() ? `${import.meta.env.VITE_REGISTRATION_PROSPECT_URL}?country=${country}&language=${locale}${canisterParam}&returnUrl=${encodeURIComponent(`${import.meta.env.VITE_MYAP_ORDER_SITE_URL}/cart/sync`)}`
            : `${import.meta.env.VITE_REGISTRATION_NON_PROSPECT_URL}?countryCode=${country}&selectedLanguage=${locale}&returnUrl=${encodeURIComponent(`${import.meta.env.VITE_MYAP_ORDER_SITE_URL}/cart/sync`)}`;
    };

    const getClickHereEmail = () => {
        let data = siteConfig.contactUsMailForMoreThan10Cyls.find(f => f.country === salesOrg.salesOrgAlias);
        if (!data) {
            return '';
        }
        return data?.email;
    };

    useEffect(() => {
        function getCurrentSalesOrg() {
            const salesOrgList = siteConfig.salesOrgData.map(s => s.salesOrgList.map(tmp => ({ ...tmp, region: s.region }))).reduce((accumulator, value) => accumulator.concat(value), []);
            const salesOrg = salesOrgList.find(i => i.region.abbrevation === region && i.salesOrgAlias === country.toUpperCase()) as SalesOrg;
            salesOrg && setSalesOrg(salesOrg);
        };

        (region && country) && getCurrentSalesOrg();
    }, [region, country]);

    return <SiteConfigContext.Provider value={{ salesOrg, getBrand, getCountryUrl, getFooterLink, getCurrencyCode, isProspectCountry, getClickHereEmail, getRegistrationLink }}>
        {children}
    </SiteConfigContext.Provider>
};

export default SiteConfigProvider;
export const useSiteConfig = () => useContext(SiteConfigContext);